import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Repeat } from 'lucide-react';
import { getRetention, qk } from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import EmptyState from '../components/EmptyState.js';
import { useActivePid } from '../components/Layout.js';
import DisaAktarDugmesi from '../components/DisaAktarDugmesi.js';
import { useRole } from '../components/AuthGate.js';
import { formatDateTime, formatNumber, formatWeekRange } from '../lib/format.js';

/**
 * §93.4 — `/retention` ("Geri Dönüş"): `aid` bazlı haftalık kohort ızgarası.
 *
 * Satır = ilk görülme haftası (son 12), sütun = +0..+8 hafta, hücre = geri gelen
 * ziyaretçi YÜZDESİ. Kohort boyutu satır başında yazar — paydası gizlenmez
 * (§93.1): 12 kişilik %50 ile 4 000 kişilik %50 aynı rengi alsa da aynı anlamı
 * taşımaz.
 *
 * RENK: tek skala, marka turuncusunun alfa basamakları. Kırmızı/yeşil BİLİNÇLİ
 * olarak yok (§93.4) — retention'da "kötü" eşiği sektöre görecelidir; panel
 * yargı rengi basmaz, yoğunluk gösterir.
 *
 * Hücre SAYIDIR, tıklanınca kişi listesi AÇILMAZ (§91.2 KARAR 2): `aid`
 * kimliksizdir ve bu sayfa o çizgiyi aşmaz.
 */

/** Hücre zemin/metin renkleri — yüzde yoğunluğu marka alfasına eşlenir. */
function hucreStil(pct: number): { background: string; color: string } {
  const alfa = Math.min(0.85, 0.06 + (pct / 100) * 0.79);
  return {
    background: `rgba(245, 102, 0, ${alfa})`,
    color: alfa > 0.45 ? '#ffffff' : '#171719',
  };
}

export default function Retention() {
  const pid = useActivePid();
  const role = useRole(pid);

  const sorgu = useQuery({
    queryKey: qk.retention(pid),
    queryFn: ({ signal }) => getRetention(pid, signal),
  });

  const haftalar = sorgu.data?.weeks ?? [];
  const ufuk = haftalar[0]?.cells.length ?? 9;

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="retention-sayfasi">
      {/* başlık */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <Repeat className="h-4 w-4 text-brand" aria-hidden="true" />
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">Geri Dönüş</h1>
          <BilgiDugmesi testId="retention-bilgi">Haftalık kohortlar: her satır o hafta İLK KEZ görülen ziyaretçiler, hücreler +k hafta sonra geri gelen yüzdesi (§93). Ziyaretçiler anonimdir — hücreler sayıdır, kişi listesi açılmaz. Bitmemiş haftalar "—" ile boş bırakılır.</BilgiDugmesi>
        </div>
        <div className="flex items-center gap-3">
          {sorgu.data !== undefined ? (
            <span className="hidden text-[12px] text-[#9ca3af] md:inline">
              Hesaplandı: {formatDateTime(sorgu.data.computed_at)} · son 12 hafta
            </span>
          ) : null}
          <DisaAktarDugmesi pid={pid} role={role} dataset="retention" />
        </div>
      </div>

      <div className="px-6 py-8">
        {sorgu.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : sorgu.isError ? (
          <span className="flex items-center gap-1.5 text-[12px] text-red-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Kohortlar yüklenemedi — sayfayı yenileyin.
          </span>
        ) : haftalar.length === 0 ? (
          <div data-testid="retention-bos">
            <EmptyState
              title="Henüz kohort oluşacak kadar veri yok"
              description="Kohortlar, SDK veri göndermeye başladıktan sonra haftadan haftaya birikir. İlk satır, ilk tam haftanın sonunda görünür."
            />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[#e5e7eb] bg-white">
            <table className="w-full border-collapse text-[12px]" data-testid="retention-izgara">
              <thead>
                <tr className="border-b border-[#e5e7eb] text-left text-[11px] uppercase tracking-wide text-[#6b7280]">
                  <th className="px-4 py-2.5 font-medium">Kohort</th>
                  <th className="px-3 py-2.5 text-right font-medium">Ziyaretçi</th>
                  {Array.from({ length: ufuk }, (_, k) => (
                    <th
                      key={k}
                      className="px-2 py-2.5 text-center font-medium"
                      title={`Kohort haftasından ${k} hafta sonra`}
                    >
                      +{k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {haftalar.map((h) => (
                  <tr key={h.cohort} className="border-b border-[#f3f4f6] last:border-b-0">
                    {/*
                      §103.2 — makine etiketi KALIR, insan tarihi YANINA gelir.
                      Etiketi silmek, export dosyasındaki satırla ekrandaki satırı
                      eşleştirilemez yapardı (§101.2 KARAR 3).
                    */}
                    <td className="whitespace-nowrap px-4 py-2 font-medium text-[#111827]">
                      <span data-testid="retention-kohort-etiket">{h.cohort}</span>
                      <span
                        className="block text-[11px] font-normal text-[#9ca3af]"
                        data-testid="retention-kohort-tarih"
                      >
                        {formatWeekRange(h.start)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-[#6b7280]">
                      {formatNumber(h.size)}
                    </td>
                    {h.cells.map((hucre, k) => (
                      <td key={k} className="p-1 text-center">
                        {hucre === null ? (
                          <span className="text-[#d1d5db]">—</span>
                        ) : (
                          <span
                            className="block rounded px-1.5 py-1.5 tabular-nums"
                            style={hucreStil(hucre)}
                            /* §93.4 ham kesir + §103.2 o hücrenin HANGİ takvim
                               haftası olduğu: sütun başlığı tek bir tarih
                               veremez (her satırın +k'sı başka haftadır). */
                            title={`${h.counts[k] ?? 0}/${h.size} · ${formatWeekRange(h.start, k)}`}
                          >
                            %{hucre.toLocaleString('tr-TR')}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* §93.1 — tanım her zaman görünür: ekran kendini açıklar, kılavuza mecbur bırakmaz. */}
        <p className="mt-3 text-[11px] text-[#9ca3af]">
          Kohort = ziyaretçinin (anonim aid) ilk görüldüğü ISO haftası. Hücre, o kohorttan +k
          hafta sonra en az bir oturumu olanların yüzdesidir; bitmemiş haftalar "—" ile boş
          bırakılır.
        </p>
      </div>
    </div>
  );
}
